import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Checkbox,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  ViewCompact as ViewCompactIcon,
  ViewStream as ViewStreamIcon,
  ViewDay as ViewDayIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../config';
import { authenticatedFetch, apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import ShiftOverrideModal from './ShiftOverrideModal';
import { useAuth } from '../contexts/AuthContext';

const normalizeRoleKey = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const formatRoleLabel = (value) => {
  const s = String(value || '').trim();
  if (!s) return '';
  return s.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
};

const getStaffRole = (t) => String(t?.role || t?.designation || t?.title || '').trim();

const StyledTableCell = styled(TableCell)(({ theme, viewMode }) => ({
  padding: viewMode === 'compact' ? theme.spacing(0.5) : 
          viewMode === 'medium' ? theme.spacing(0.75) : 
          theme.spacing(1),
  transition: 'all 0.2s ease-in-out',
  '&.header': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    textAlign: 'center',
    borderRight: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
    minWidth: viewMode === 'compact' ? 80 : 
             viewMode === 'medium' ? 90 : 
             100,
    fontSize: viewMode === 'compact' ? '0.75rem' : 
             viewMode === 'medium' ? '0.875rem' : 
             '1rem',
    padding: theme.spacing(1.5),
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  '&.name-cell': {
    backgroundColor: theme.palette.background.paper,
    fontWeight: 500,
    position: 'sticky',
    left: 0,
    zIndex: 2,
    boxShadow: 'none',
    borderRight: `1px solid ${theme.palette.divider}`,
    minWidth: viewMode === 'compact' ? 200 : 
             viewMode === 'medium' ? 250 : 
             300,
    fontSize: viewMode === 'compact' ? '0.75rem' : 
             viewMode === 'medium' ? '0.875rem' : 
             '1rem',
    padding: theme.spacing(1, 2),
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  '&.shift-cell': {
    textAlign: 'center',
    padding: viewMode === 'compact' ? theme.spacing(0.5) : 
             viewMode === 'medium' ? theme.spacing(0.75) : 
             theme.spacing(1),
    borderRight: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.light, 0.1),
      transform: 'scale(1.02)'
    }
  },
  '&.today': {
    backgroundColor: alpha(theme.palette.primary.light, 0.1),
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '2px',
      backgroundColor: theme.palette.primary.main
    }
  },
  '&.weekend': {
    backgroundColor: alpha(theme.palette.grey[100], 0.5),
  }
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
}));

const NavigationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ViewControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
}));

const StyledChip = styled(Chip)(({ theme, shiftType }) => ({
  borderRadius: theme.shape.borderRadius,
  fontWeight: 500,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  ...(shiftType === 'morning' && {
    backgroundColor: alpha(theme.palette.info.light, 0.1),
    color: theme.palette.info.dark,
    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
  }),
  ...(shiftType === 'afternoon' && {
    backgroundColor: alpha(theme.palette.warning.light, 0.1),
    color: theme.palette.warning.dark,
    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
  }),
  ...(shiftType === 'evening' && {
    backgroundColor: alpha(theme.palette.success.light, 0.1),
    color: theme.palette.success.dark,
    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
  }),
}));

const ShiftCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.02)',
  },
}));

/** Merge per-therapist override times into shift details for roster display */
const getAssignedShiftForTherapist = (dateAssignments, therapistName) => {
  if (!dateAssignments || !therapistName) return null;

  for (const shiftData of Object.values(dateAssignments)) {
    if (!shiftData?.therapists?.includes(therapistName)) continue;

    const baseDetails = shiftData.shift_details;
    if (!baseDetails) return null;

    const therapistOverride = shiftData.therapist_overrides?.[therapistName];
    if (therapistOverride?.has_override) {
      return {
        ...baseDetails,
        start_time: therapistOverride.start_time || baseDetails.start_time,
        end_time: therapistOverride.end_time || baseDetails.end_time,
        breaks: therapistOverride.breaks || baseDetails.breaks,
        has_custom_schedule: true,
      };
    }
    return baseDetails;
  }
  return null;
};

const Roster = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('duty', 'edit');
  
  const [startDate, setStartDate] = useState(dayjs());
  const [therapists, setTherapists] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedShift, setSelectedShift] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [viewMode, setViewMode] = useState('normal');
  const [openBatchDialog, setOpenBatchDialog] = useState(false);
  const [selectedTherapists, setSelectedTherapists] = useState([]);
  const [batchSchedule, setBatchSchedule] = useState({
    startDate: '',
    endDate: '',
    shift: ''
  });
  const [singleCellSchedule, setSingleCellSchedule] = useState({
    startDate: '',
    endDate: ''
  });
  const [roleFilters, setRoleFilters] = useState([]);
  const [nameSearch, setNameSearch] = useState('');
  const [openOverrideModal, setOpenOverrideModal] = useState(false);

  const fetchTherapists = async () => {
    try {
      const response = await apiGet('/api/therapists');
      if (!response.ok) throw new Error('Failed to fetch therapists');
      const data = await response.json();
      setTherapists(data);
    } catch (error) {
      console.error('Error fetching therapists:', error);
      enqueueSnackbar('Failed to fetch therapists', { variant: 'error' });
    }
  };

  const roleOptions = useMemo(() => {
    const roles = new Map();
    (therapists || [])
      .filter(t => String(t.department || '').toLowerCase() === 'wellness')
      .forEach(t => {
        const effective = getStaffRole(t);
        if (!effective) return;
        const key = normalizeRoleKey(effective);
        if (!roles.has(key)) roles.set(key, formatRoleLabel(effective));
      });
    return Array.from(roles.values()).sort();
  }, [therapists]);

  const visibleTherapists = useMemo(() => (
    (therapists || [])
      .filter(t => String(t.department || '').toLowerCase() === 'wellness')
      .filter(t => {
        if (roleFilters.length === 0) return true;
        const staffRole = normalizeRoleKey(getStaffRole(t));
        return roleFilters.some((role) => normalizeRoleKey(role) === staffRole);
      })
      .filter(t => !nameSearch || String(t.name || '').toLowerCase().includes(nameSearch.toLowerCase()))
  ), [therapists, roleFilters, nameSearch]);

  const visibleTherapistNames = useMemo(
    () => visibleTherapists.map(t => t.name),
    [visibleTherapists],
  );

  const selectedVisibleCount = useMemo(
    () => visibleTherapistNames.filter(name => selectedTherapists.includes(name)).length,
    [visibleTherapistNames, selectedTherapists],
  );

  const fetchShifts = async () => {
    try {
      const response = await apiGet('/api/shifts');
      if (!response.ok) throw new Error('Failed to fetch shifts');
      const data = await response.json();
      console.log('Fetched shifts:', data); // Debug log
      setShifts(data);
    } catch (error) {
      console.error('Error fetching shifts:', error);
      enqueueSnackbar('Failed to fetch shifts', { variant: 'error' });
    }
  };

  const fetchAssignments = async () => {
    try {
      // Only fetch assignments for the visible date range (22 days)
      const start = startDate.format('YYYY-MM-DD');
      const end = startDate.add(21, 'day').format('YYYY-MM-DD'); // 22 days inclusive
      
      const response = await apiGet(`/api/roster/assignments?start_date=${start}&end_date=${end}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch assignments');
      }
      const data = await response.json();
      console.log(`Fetched assignments for ${start} to ${end}:`, data); // Debug log
      setAssignments(data || {});
    } catch (error) {
      console.error('Error fetching assignments:', error);
      enqueueSnackbar(error.message || 'Failed to fetch assignments', { variant: 'error' });
      setAssignments({});
    }
  };

  useEffect(() => {
    const init = async () => {
      await fetchShifts(); // Fetch shifts first
      await fetchTherapists();
      await fetchAssignments();
    };
    init();
  }, []);

  useEffect(() => {
    // Re-fetch assignments when date changes
    fetchAssignments();
  }, [startDate]);

  const handleCellClick = (therapist, date) => {
    // Check if user has edit permission
    if (!canEdit) {
      enqueueSnackbar('You do not have permission to edit roster assignments', { variant: 'warning' });
      return;
    }
    
    setSelectedCell({ therapist, date });
    // Preselect current shift if any
    const dateAssignments = assignments[date] || {};
    let currentShiftCode = '';
    let currentShiftDetails = null;
    Object.entries(dateAssignments).forEach(([shiftCode, shiftData]) => {
      if (shiftData.therapists && shiftData.therapists.includes(therapist.name)) {
        currentShiftCode = shiftCode;
        currentShiftDetails = getAssignedShiftForTherapist(dateAssignments, therapist.name);
      }
    });
    setSelectedShift(currentShiftCode);
    
    // If there's an existing shift assignment, open the full override modal
    // Otherwise, open the minimal dialog for first-time assignment
    if (currentShiftDetails) {
      setSelectedCell({ 
        therapist, 
        date,
        currentShift: currentShiftDetails
      });
      setOpenOverrideModal(true);
    } else {
      // Empty slot - open minimal dialog with date range and shift selection
      setSingleCellSchedule({
        startDate: date,
        endDate: date
      });
      setOpenDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCell(null);
    setSelectedShift('');
    setSingleCellSchedule({
      startDate: '',
      endDate: ''
    });
  };

  const handleShiftSelect = async () => {
    if (!selectedCell || !selectedShift) return;
    
    // Check if date range is provided
    if (singleCellSchedule.startDate && singleCellSchedule.endDate) {
      // Use batch API for date range
      try {
        const { therapist } = selectedCell;
        const ids = [therapist.name];
        
        const response = await apiPost('/api/shifts/assign/batch', {
          shift_code: selectedShift,
          start_date: singleCellSchedule.startDate,
          end_date: singleCellSchedule.endDate,
          therapist_ids: ids
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to assign shifts');
        }
        
        console.log('Successfully assigned shifts for date range');
        handleCloseDialog();
        enqueueSnackbar('Shifts assigned successfully', { variant: 'success' });
        fetchAssignments();
      } catch (error) {
        console.error('Error assigning shifts:', error);
        enqueueSnackbar(error.message || 'Failed to assign shifts', { variant: 'error' });
      }
      return;
    }

    // Original single date logic
    try {
      const { therapist, date } = selectedCell;
      const ids = selectedTherapists.length > 0 ? selectedTherapists : [therapist.name];
      console.log('Assigning shift:', { ids, date, selectedShift });
      
      const response = await apiPost('/api/shifts/assign', {
        shift_code: selectedShift,
        date: date,
        therapist_ids: ids
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to assign shift');
      }
      
      // Get the response data (not used yet but kept for debugging/telemetry)
      const assignmentData = await response.json();
      
      // Immediately update the local assignments state (merge + de-dup; remove from other shifts on same date)
      setAssignments(prevAssignments => {
        const newAssignments = { ...prevAssignments };
        const dateKey = date;
        if (!newAssignments[dateKey]) newAssignments[dateKey] = {};

        // Ensure shift_details exists for selected shift
        const shiftDetails = shifts.find(s => s.shift_code === selectedShift);

        // Remove selected therapists from other shifts for the same date
        Object.entries(newAssignments[dateKey]).forEach(([sc, payload]) => {
          if (sc === selectedShift) return;
          const list = Array.isArray(payload?.therapists) ? payload.therapists : [];
          newAssignments[dateKey][sc] = {
            ...payload,
            therapists: list.filter(name => !ids.includes(name))
          };
        });

        const existing = newAssignments[dateKey][selectedShift] || {
          shift_details: {
            shift_code: selectedShift,
            name: shiftDetails?.name || '',
            color: shiftDetails?.color || '#f0f0f0',
            start_time: shiftDetails?.start_time || '',
            end_time: shiftDetails?.end_time || ''
          },
          therapists: []
        };

        const mergedTherapists = Array.from(new Set([...(existing.therapists || []), ...ids]));
        newAssignments[dateKey][selectedShift] = {
          ...existing,
          therapists: mergedTherapists
        };

        return newAssignments;
      });
      
      console.log('Successfully assigned shift');
      handleCloseDialog();
      enqueueSnackbar('Shift assigned successfully', { variant: 'success' });
      
      // Still fetch assignments to ensure consistency
      fetchAssignments();
    } catch (error) {
      console.error('Error assigning shift:', error);
      enqueueSnackbar(error.message || 'Failed to assign shift', { variant: 'error' });
    }
  };

  const handlePrevWeek = () => {
    setStartDate(prev => prev.subtract(7, 'day'));
  };

  const handleNextWeek = () => {
    setStartDate(prev => prev.add(7, 'day'));
  };

  const getDatesArray = () => {
    const dates = [];
    for (let i = 0; i < 22; i++) {
      dates.push(startDate.add(i, 'day'));
    }
    return dates;
  };

  const renderCell = (therapist, date) => {
    const dateStr = date.format('YYYY-MM-DD');
    const dateAssignments = assignments[dateStr] || {};
    const isWeekend = date.day() === 0 || date.day() === 6;
    console.log('Rendering cell for:', { dateStr, therapist, assignments: dateAssignments }); // Debug log
    
    // Find which shift this therapist is assigned to (include per-therapist overrides)
    const assignedShift = getAssignedShiftForTherapist(dateAssignments, therapist.name);
    
    console.log('Found shift:', assignedShift); // Debug log

    // Determine background color
    let bgColor = 'transparent';
    if (assignedShift) {
      bgColor = alpha(assignedShift.color || '#1976d2', 0.1);
    } else if (isWeekend) {
      bgColor = '#fff3e0'; // Light orange for weekends
    }

    return (
      <StyledTableCell 
        key={`${therapist.name}-${dateStr}`}
        onClick={() => handleCellClick(therapist, dateStr)}
        className="shift-cell"
        viewMode={viewMode}
        sx={{
          bgcolor: bgColor,
          '&:hover': {
            bgcolor: canEdit ? (assignedShift ? alpha(assignedShift.color || '#1976d2', 0.2) : alpha('#000', 0.04)) : bgColor,
          },
          cursor: canEdit ? 'pointer' : 'not-allowed',
          minWidth: viewMode === 'compact' ? 60 : 80,
          maxWidth: viewMode === 'compact' ? 80 : 100,
          padding: viewMode === 'compact' ? '2px' : '4px',
          height: viewMode === 'compact' ? '40px' : '60px',
          opacity: canEdit ? 1 : 0.7,
        }}
      >
        {assignedShift && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 0.25
          }}>
            <Typography variant="caption" sx={{ 
              fontWeight: 500,
              fontSize: viewMode === 'compact' ? '0.65rem' : '0.75rem'
            }}>
              {assignedShift.shift_code}
              {assignedShift.has_custom_schedule ? ' ⚡' : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ 
              fontSize: viewMode === 'compact' ? '0.6rem' : '0.65rem',
              display: viewMode === 'compact' ? 'none' : 'block'
            }}>
              {assignedShift.start_time} - {assignedShift.end_time}
            </Typography>
          </Box>
        )}
      </StyledTableCell>
    );
  };

  const handleBatchSchedule = async () => {
    if (!batchSchedule.shift || !batchSchedule.startDate || !batchSchedule.endDate || selectedTherapists.length === 0) {
      enqueueSnackbar('Please select therapists and fill in all fields', { variant: 'error' });
      return;
    }

    try {
      const response = await apiPost('/api/shifts/assign/batch', {
        shift_code: batchSchedule.shift,
        start_date: batchSchedule.startDate,
        end_date: batchSchedule.endDate,
        therapist_ids: selectedTherapists
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to assign shifts');
      }

      await fetchAssignments();
      handleCloseBatchDialog();
      enqueueSnackbar('Shifts assigned successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error assigning shifts:', error);
      enqueueSnackbar(error.message || 'Failed to assign shifts', { variant: 'error' });
    }
  };

  const handleOpenBatchDialog = () => {
    if (selectedTherapists.length === 0) {
      enqueueSnackbar('Please select at least one therapist', { variant: 'warning' });
      return;
    }
    setBatchSchedule({
      ...batchSchedule,
      startDate: dayjs().format('YYYY-MM-DD'),
      endDate: dayjs().add(7, 'day').format('YYYY-MM-DD')
    });
    setOpenBatchDialog(true);
  };

  const handleCloseBatchDialog = () => {
    setOpenBatchDialog(false);
    setBatchSchedule({
      startDate: '',
      endDate: '',
      shift: ''
    });
  };

  const handleTherapistSelect = (therapistName) => {
    setSelectedTherapists(prev => {
      if (prev.includes(therapistName)) {
        return prev.filter(name => name !== therapistName);
      }
      return [...prev, therapistName];
    });
  };

  const handleSelectAllTherapists = (event) => {
    if (event.target.checked) {
      setSelectedTherapists(visibleTherapistNames);
    } else {
      setSelectedTherapists(prev => prev.filter(name => !visibleTherapistNames.includes(name)));
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f6f6f6', minHeight: '100%', height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#171717' }}>Roster</Typography>
      <StyledPaper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px', bgcolor: '#fff', display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        <NavigationContainer>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handlePrevWeek}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {startDate.format('MMMM D')} - {startDate.add(6, 'day').format('MMMM D, YYYY')}
            </Typography>
            <IconButton onClick={handleNextWeek}>
              <ChevronRightIcon />
            </IconButton>
            <TextField
              size="small"
              placeholder="Search therapist..."
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
              sx={{ ml: 2, minWidth: 200 }}
            />
          </Box>
          <ViewControls>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="compact">
                <Tooltip title="Compact View">
                  <ViewCompactIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="normal">
                <Tooltip title="Normal View">
                  <ViewDayIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title={canEdit ? "Batch Schedule Selected" : "You don't have permission to edit roster"}>
              <span>
                <IconButton 
                  onClick={handleOpenBatchDialog}
                  disabled={!canEdit || selectedTherapists.length === 0}
                  sx={(theme) => ({
                    color: canEdit && selectedTherapists.length > 0 ? theme.palette.primary.main : theme.palette.action.disabled,
                    '&:hover': {
                      backgroundColor: canEdit && selectedTherapists.length > 0 ? alpha(theme.palette.primary.main, 0.1) : undefined,
                    }
                  })}
                >
                  <DateRangeIcon />
                </IconButton>
              </span>
            </Tooltip>
          </ViewControls>
        </NavigationContainer>

        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell 
                  className="name-header-cell" 
                  sx={{ 
                    width: '200px',
                    position: 'sticky !important',
                    left: '0 !important',
                    top: '0 !important',
                    zIndex: '3 !important',
                    backgroundColor: '#fff !important',
                    borderBottom: '2px solid #e5e7eb',
                    fontWeight: 600,
                    verticalAlign: 'middle'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Checkbox
                      size="small"
                      disabled={!canEdit || visibleTherapistNames.length === 0}
                      indeterminate={selectedVisibleCount > 0 && selectedVisibleCount < visibleTherapistNames.length}
                      checked={visibleTherapistNames.length > 0 && selectedVisibleCount === visibleTherapistNames.length}
                      onChange={handleSelectAllTherapists}
                    />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <Select
                        multiple
                        value={roleFilters}
                        onChange={(e) => {
                          const value = e.target.value;
                          setRoleFilters(typeof value === 'string' ? value.split(',') : value);
                        }}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected.length) return 'All Roles';
                          if (selected.length === 1) return selected[0];
                          return `${selected.length} roles`;
                        }}
                      >
                        {roleOptions.map((r) => (
                          <MenuItem key={r} value={r}>
                            <Checkbox size="small" checked={roleFilters.indexOf(r) > -1} />
                            {r}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </StyledTableCell>
                {getDatesArray().map((date) => {
                  const isWeekend = date.day() === 0 || date.day() === 6;
                  return (
                    <StyledTableCell
                      key={date.format('YYYY-MM-DD')}
                      align="center"
                      className={isWeekend ? 'weekend' : ''}
                      sx={{
                        position: 'sticky !important',
                        top: '0 !important',
                        zIndex: '2 !important',
                        backgroundColor: `${isWeekend ? '#fff3e0' : '#fff'} !important`,
                        borderBottom: '2px solid #e5e7eb',
                        fontWeight: 600
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#171717' }}>
                        {date.format('ddd')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {date.format('MMM D')}
                      </Typography>
                    </StyledTableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleTherapists.map((therapist) => (
                <TableRow key={therapist.name} hover>
                  <StyledTableCell 
                    className="name-cell" 
                    viewMode={viewMode}
                    sx={{
                      position: 'sticky !important',
                      left: '0 !important',
                      zIndex: '1 !important',
                      backgroundColor: '#fff !important',
                      verticalAlign: 'middle'
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1
                    }}>
                      <Checkbox
                        size="small"
                        disabled={!canEdit}
                        checked={selectedTherapists.includes(therapist.name)}
                        onChange={() => handleTherapistSelect(therapist.name)}
                      />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500,
                          color: 'text.primary'
                        }}
                      >
                        {therapist.name}
                      </Typography>
                      {therapist.rank && (
                        <Chip 
                          label={`R${therapist.rank}`} 
                          size="small"
                          sx={(theme) => ({ 
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            minWidth: 'unset'
                          })}
                        />
                      )}
                    </Box>
                  </StyledTableCell>
                  {getDatesArray().map((date) => renderCell(therapist, date))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </StyledPaper>

      {/* Batch Assignment Dialog */}
      <Dialog open={openBatchDialog} onClose={() => setOpenBatchDialog(false)}>
        <DialogTitle>Batch Assign Shifts</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Selected Therapists: {selectedTherapists.length}
            </Typography>
            <TextField
              label="Start Date"
              type="date"
              value={batchSchedule.startDate}
              onChange={(e) => setBatchSchedule(prev => ({ ...prev, startDate: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              value={batchSchedule.endDate}
              onChange={(e) => setBatchSchedule(prev => ({ ...prev, endDate: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth>
              <InputLabel>Select Shift</InputLabel>
              <Select
                value={batchSchedule.shift}
                onChange={(e) => setBatchSchedule(prev => ({ ...prev, shift: e.target.value }))}
                label="Select Shift"
              >
                {shifts.map((shift) => (
                  <MenuItem 
                    key={shift.shift_code} 
                    value={shift.shift_code}
                    sx={(theme) => ({
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      padding: theme.spacing(1.5),
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      '&:last-child': {
                        borderBottom: 'none'
                      },
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08)
                      },
                      '&.Mui-selected': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.16)
                        }
                      }
                    })}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mb: 0.5 }}>
                      <Chip 
                        label={shift.shift_code}
                        size="small"
                        sx={{
                          backgroundColor: shift.color || '#E4C984',
                          color: '#171717',
                          fontWeight: 600,
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          minWidth: '50px',
                          justifyContent: 'center'
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
                        {shift.name}
                      </Typography>
                    </Box>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        ml: 0.5,
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      🕐 {shift.start_time} - {shift.end_time}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBatchDialog(false)}>Cancel</Button>
          <Button onClick={handleBatchSchedule} variant="contained" color="primary">
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 400,
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2
        }}>
          Assign Shift
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              value={singleCellSchedule.startDate}
              onChange={(e) => setSingleCellSchedule(prev => ({ ...prev, startDate: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              value={singleCellSchedule.endDate}
              onChange={(e) => setSingleCellSchedule(prev => ({ ...prev, endDate: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Shift</InputLabel>
            <Select
              value={selectedShift}
              onChange={(e) => setSelectedShift(e.target.value)}
              label="Select Shift"
            >
              {shifts.map((shift) => (
                <MenuItem 
                  key={shift.shift_code} 
                  value={shift.shift_code}
                  sx={(theme) => ({
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    padding: theme.spacing(1.5),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    '&:last-child': {
                      borderBottom: 'none'
                    },
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08)
                    },
                    '&.Mui-selected': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.12),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.16)
                      }
                    }
                  })}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mb: 0.5 }}>
                    <Chip 
                      label={shift.shift_code}
                      size="small"
                      sx={{
                        backgroundColor: shift.color || '#E4C984',
                        color: '#171717',
                        fontWeight: 600,
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        minWidth: '50px',
                        justifyContent: 'center'
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
                      {shift.name}
                    </Typography>
                  </Box>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      ml: 0.5,
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    🕐 {shift.start_time} - {shift.end_time}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleShiftSelect} 
            variant="contained" 
            disabled={!selectedShift}
            sx={{
              '&:not(:disabled)': {
                background: 'linear-gradient(45deg, #C7AE6E 0%, #b49a5f 100%)',
                color: '#171717',
                boxShadow: '0 3px 5px 2px rgba(199, 174, 110, .3)',
              }
            }}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Shift Override Modal */}
      <ShiftOverrideModal
        open={openOverrideModal}
        onClose={() => {
          setOpenOverrideModal(false);
          setSelectedCell(null);
        }}
        therapist={selectedCell?.therapist}
        date={selectedCell?.date}
        currentShift={selectedCell?.currentShift}
        shifts={shifts}
        onSave={() => {
          fetchAssignments();
          setOpenOverrideModal(false);
          setSelectedCell(null);
        }}
      />

    </Box>
  );
};

export default Roster;
